import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "../components/UI";

export default function ChangePassword({ forced: forcedProp = false }) {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Either the prop says forced, or we landed here via the ProtectedRoute redirect.
  const forced = forcedProp || Boolean(location.state?.forced) || Boolean(user?.must_change_password);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match."); return; }
    setBusy(true);
    const r = await changePassword(current, next);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Couldn't change password");
      return;
    }
    toast.success("Password updated");
    navigate("/");
  };

  return (
    <div className={forced ? "min-h-screen login-canvas grain flex items-center justify-center px-4 py-12" : ""}>
      <div className={forced ? "max-w-[560px] w-full" : ""}>
        <PageHeader
          testid="change-password-header"
          title={forced ? "Set a new password" : "Change your password"}
          subtitle={
            forced
              ? `Welcome${user?.name ? ", " + user.name : ""} — for security, please replace the temporary password before continuing.`
              : "Update the password you use to sign in."
          }
        />

        <form onSubmit={submit} className="card p-6 max-w-xl space-y-4" data-testid="change-password-form">
          <div className="flex items-start gap-2 p-3 rounded-xl bg-brand-muted text-brand-ink2 text-sm">
            <ShieldCheck size={14} className="mt-0.5 text-brand-gold" />
            <span>
              Strong passwords are at least 8 characters and contain a letter and a number.
              Avoid reusing passwords from other services.
            </span>
          </div>

          <label className="block">
            <span className="label">Current password</span>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
              <input
                data-testid="cp-current"
                type="password"
                required
                className="input-base pl-10"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          </label>

          <label className="block">
            <span className="label">New password</span>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
              <input
                data-testid="cp-new"
                type="password"
                required
                minLength={8}
                className="input-base pl-10"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters incl. a number"
              />
            </div>
          </label>

          <label className="block">
            <span className="label">Confirm new password</span>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
              <input
                data-testid="cp-confirm"
                type="password"
                required
                minLength={8}
                className="input-base pl-10"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          </label>

          <div className="flex items-center gap-2">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="cp-submit">
              {busy ? (<><Loader2 size={16} className="animate-spin" /> Saving…</>) : (<>Update password <ArrowRight size={16}/></>)}
            </button>
            {forced && (
              <button type="button" onClick={() => { logout(); navigate("/login"); }} className="btn-ghost text-sm" data-testid="cp-cancel">
                Sign out instead
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
